import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { ProductStatesClient, ProductStateRequest, ProductStateResponse } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface ProductState extends ProductStateResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    productStateList: ProductState[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    productStateList: [],
};

const productStateSlice = createSlice({
    name: 'productState',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getProductStateListSuccess: (state, action: PayloadAction<ProductState[]>) => {
            state.productStateList = action.payload;
            state.isLoading = false;
        },
        addNewProductStateSuccess: (state, action: PayloadAction<ProductState>) => {
            const newItem = action.payload;
            state.productStateList = [newItem, ...state.productStateList];
            state.isLoading = false;
        },
        updateProductStateSuccess: (state, action: PayloadAction<ProductState>) => {
            const updatedItem = action.payload;
            const index = state.productStateList.findIndex(item => item.productStateId === updatedItem.productStateId);
            state.productStateList = [...state.productStateList.slice(0, index), updatedItem, ...state.productStateList.slice(index + 1)];
            state.isLoading = false;
        },
    },
});

// Reducer
export default productStateSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getProductStateListSuccess,
    addNewProductStateSuccess,
    updateProductStateSuccess,
} = productStateSlice.actions;

// for useSelector
export const selectProductState = (state: RootState) => state.productState as State;

// ----------------------------------------------------------------------
export function getAllProductState(ignoreCache: boolean = false) {
    return async (dispatch, getState) => {
        try {
            const { productStateList } = selectProductState(getState());
            if (ignoreCache || productStateList.length === 0) {
                dispatch(productStateSlice.actions.startLoading());
                const client = new ProductStatesClient(undefined, axios);
                const response = await client.getAllProductState(false);
                dispatch(productStateSlice.actions.getProductStateListSuccess(response));
            }
        } catch (error) {
            dispatch(productStateSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createProductState(productState: ProductState, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productStateSlice.actions.startLoading());
        try {
            const { productStateId, ...payload } = productState;
            const request = payload as ProductStateRequest;
            const client = new ProductStatesClient(undefined, axios);
            const response = await client.createProductState(request);
            dispatch(productStateSlice.actions.addNewProductStateSuccess(response));
            if (showNotification) {
                toast.success(`${response.productStateCode} created successfully!`);
            }
        } catch (error) {
            dispatch(productStateSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateProductState(productState: ProductState, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productStateSlice.actions.startLoading());
        try {
            const { productStateId, ...payload } = productState;
            const request = payload as ProductStateRequest;
            const client = new ProductStatesClient(undefined, axios);
            const response = await client.editProductState(productStateId, request);
            dispatch(productStateSlice.actions.updateProductStateSuccess(response));
            if (showNotification) {
                toast.success(`${response.productStateCode} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
            }
        } catch (error) {
            dispatch(productStateSlice.actions.hasError(true));
            handleError(error);
        }
    };
}